import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import cn from 'classnames'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import Modal from '../Unknown/Modal'
import translate from '../../i18n/translate'
import { getApiErrorMessage, robotSmsPolicyApi } from '../../modules/services/robotSmsPolicyService'
import { formatFileSize } from './utils'
import useStyles from './useStyles'

const MAX_FILE_SIZE = 20 * 1024 * 1024
const VERSION_PATTERN = /^[\w.-]{1,32}$/

// "1.0" -> "1.1", "v2" -> "v3"; anything else is left for the admin to type
const suggestNextVersion = (lastVersion?: string) => {
  const match = lastVersion?.match(/^(.*?)(\d+)$/)

  return match ? `${match[1]}${Number(match[2]) + 1}` : ''
}

type Props = {
  existingVersions: string[]
  lastVersion?: string
  onClose: () => void
}

// Mounted only while open, so the initial state is the reset
const UploadRobotSmsPolicyModal = ({ existingVersions, lastVersion, onClose }: Props) => {
  const intl = useIntl()
  const classes = useStyles()

  const [version, setVersion] = useState(() => suggestNextVersion(lastVersion))
  const [file, setFile] = useState<File | null>(null)
  const [isVersionTouched, setIsVersionTouched] = useState(false)

  const [uploadVersion, { isLoading }] = robotSmsPolicyApi.useUploadRobotSmsPolicyVersionMutation()

  const trimmedVersion = version.trim()
  const versionError = !VERSION_PATTERN.test(trimmedVersion)
    ? 'robotSmsPolicyVersionInvalid'
    : existingVersions.includes(trimmedVersion)
      ? 'robotSmsPolicyVersionExists'
      : null

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: isLoading,
    onDrop: (accepted, rejected) => {
      if (rejected.length || !accepted[0]) {
        toast.error(intl.formatMessage({ id: 'robotSmsPolicyFileInvalid' }))
        return
      }

      setFile(accepted[0])
    },
  })

  const onSubmit = async () => {
    setIsVersionTouched(true)
    if (versionError || !file) return

    const body = new FormData()
    body.append('version', trimmedVersion)
    body.append('file', file, file.name)

    try {
      await uploadVersion(body).unwrap()
      toast.success(intl.formatMessage({ id: 'robotSmsPolicyUploadSuccess' }, { version: trimmedVersion }))
      onClose()
    } catch (error) {
      toast.error(getApiErrorMessage(error) || intl.formatMessage({ id: 'robotSmsPolicyUploadFailed' }))
    }
  }

  return (
    <Modal title={translate('robotSmsPolicyUploadTitle')} visible style={{ maxWidth: '520px' }} setVisible={onClose}>
      <Box my={2} p={2} display="flex" flexDirection="column" alignItems="center" width={1} gap={2}>
        <TextField
          fullWidth
          size="small"
          color="warning"
          label={intl.formatMessage({ id: 'robotSmsPolicyVersionLabel' })}
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          onBlur={() => setIsVersionTouched(true)}
          error={isVersionTouched && !!versionError}
          helperText={isVersionTouched && versionError ? intl.formatMessage({ id: versionError }) : ' '}
        />

        <div {...getRootProps({ className: cn(classes.Dropzone, { [classes.DropzoneActive]: isDragActive }) })}>
          <input {...getInputProps()} />
          <Typography fontWeight={600}>
            {file ? `${file.name} ${formatFileSize(file.size)}` : translate('robotSmsPolicyDrop')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('robotSmsPolicyFileHint')}
          </Typography>
        </div>

        <Alert severity="warning" sx={{ width: '100%' }}>
          {translate('robotSmsPolicyUploadWarning')}
        </Alert>

        <Button size="large" variant="contained" color="warning" onClick={onSubmit} disabled={isLoading || !file || !!versionError}>
          {translate('robotSmsPolicyUploadSubmit')}
        </Button>
      </Box>
    </Modal>
  )
}

export default UploadRobotSmsPolicyModal
